import { AlertTriangle, TrendingUp, TrendingDown, Asterisk, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency as fmtCurrency, useCurrency } from '@/lib/currency';
import { useIsMobile } from '@/lib/useIsMobile';

function formatDate(dateStr, horizonDays) {
  const date = new Date(dateStr + 'T00:00:00');
  const currentYear = new Date().getFullYear();
  const dateYear = date.getFullYear();
  
  // Only show year in 2-year view (730 days) and only for future years
  if (horizonDays >= 730 && dateYear > currentYear) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }) + " '" + String(dateYear).slice(-2);
  }
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function LedgerRow({ entry, globalIndex, horizonDays, onEdit }) {
  const [currencyCode] = useCurrency();
  const formatCurrency = (v) => fmtCurrency(v, currencyCode);
  const isIncome = entry.amount > 0;
  const hasLowAlert = entry.below_min_alert;
  const hasHighAlert = entry.above_max_alert;
  
  return (
    <div
      key={entry.date + '-' + entry.transaction_id + '-' + globalIndex}
      data-testid={'ledger-row-' + globalIndex}
      className={cn(
        'group flex items-center px-3 py-1.5',
        'border-b border-border/30 last:border-b-0',
        'hover:bg-accent/40 transition-colors',
        entry.is_hypothetical && 'bg-purple-500/25 border-l-2 border-l-purple-500',
        !entry.is_hypothetical && hasLowAlert && 'bg-destructive/10',
        !entry.is_hypothetical && hasHighAlert && 'bg-yellow-500/10'
      )}
    >
      <span className="w-[62px] flex-shrink-0 text-[11px] text-muted-foreground">
        {formatDate(entry.date, horizonDays)}
      </span>
      
      <span className="flex-1 min-w-0 text-[12px] truncate flex items-center gap-0.5">
        <span className="truncate">{entry.name}</span>
        {entry.is_hypothetical && (
          <Asterisk className="w-2.5 h-2.5 text-purple-500 flex-shrink-0" />
        )}
      </span>
      
      <span
        className={cn(
          'w-[82px] flex-shrink-0 text-right font-mono text-[11px]',
          isIncome ? 'text-green-600 dark:text-green-400 font-medium' : 'text-foreground'
        )}
      >
        {isIncome ? '+' : ''}{formatCurrency(entry.amount)}
      </span>

      {onEdit ? (
        <button
          onClick={onEdit}
          data-testid={'ledger-edit-' + globalIndex}
          className="w-[18px] flex-shrink-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
          title="Edit transaction"
        >
          <Pencil className="w-3 h-3" />
        </button>
      ) : (
        <span className="w-[18px] flex-shrink-0" />
      )}
      
      <span
        className={cn(
          'w-[95px] flex-shrink-0 text-right font-mono text-[11px] font-semibold flex items-center justify-end gap-1',
          hasLowAlert && 'text-destructive',
          hasHighAlert && 'text-yellow-600 dark:text-yellow-400',
          !hasLowAlert && !hasHighAlert && 'text-foreground'
        )}
      >
        {hasLowAlert && <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
        {hasHighAlert && <TrendingUp className="w-3 h-3 flex-shrink-0" />}
        {formatCurrency(entry.balance)}
      </span>
    </div>
  );
}

function LedgerColumn({ column, colIndex, entriesPerColumn, horizonDays, onEditTransaction, transactions }) {
  const [currencyCode] = useCurrency();
  const formatCurrency = (v) => fmtCurrency(v, currencyCode);
  const rowElements = [];
  for (let i = 0; i < column.entries.length; i++) {
    const entry = column.entries[i];
    const globalIndex = colIndex * entriesPerColumn + i;

    let handleEdit = null;
    if (onEditTransaction && entry.transaction_id) {
      const tx = transactions.find(function(t) { return t.id === entry.transaction_id; });
      if (tx) {
        handleEdit = function() { onEditTransaction(tx); };
      }
    }

    rowElements.push(
      <LedgerRow 
        key={entry.date + '-' + entry.transaction_id + '-' + globalIndex}
        entry={entry} 
        globalIndex={globalIndex}
        horizonDays={horizonDays}
        onEdit={handleEdit}
      />
    );
  }
  
  return (
    <div className="ledger-column min-w-0 overflow-hidden flex flex-col">
      <div className="flex items-center px-3 py-2 bg-muted rounded-t-lg border border-b-0 border-border text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
        <span className="w-[62px] flex-shrink-0">Date</span>
        <span className="flex-1 min-w-0">Description</span>
        <span className="w-[82px] text-right flex-shrink-0">Amount</span>
        <span className="w-[18px] flex-shrink-0" />
        <span className="w-[95px] text-right flex-shrink-0">Balance</span>
      </div>
      
      <div className="border border-border bg-card flex-1">
        {rowElements}
      </div>
      
      <div className="border border-t-0 border-border rounded-b-lg bg-muted/50 px-3 py-2">
        <div className="flex items-center justify-between text-[10px]">
          <div className="flex items-center gap-1 text-muted-foreground">
            <TrendingDown className="w-3 h-3" />
            <span>Low:</span>
            <span className="font-mono font-semibold text-foreground">{formatCurrency(column.minBalance)}</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <TrendingUp className="w-3 h-3" />
            <span>High:</span>
            <span className="font-mono font-semibold text-foreground">{formatCurrency(column.maxBalance)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LedgerTable({ entries, horizonDays, transactions, onEditTransaction }) {
  // On mobile (<md breakpoint) render the ledger as a single block — one
  // header, one body, one footer — so the user gets a continuous read instead
  // of 4 stacked mini-tables with their own headers/footers. Desktop keeps
  // the 4-column "reading like a book" layout untouched.
  const isMobile = useIsMobile();
  const columnCount = isMobile ? 1 : 4;
  const entriesPerColumn = Math.ceil(entries.length / columnCount);
  
  const columns = [];
  for (let i = 0; i < columnCount; i++) {
    const start = i * entriesPerColumn;
    const end = start + entriesPerColumn;
    const colEntries = entries.slice(start, end);
    if (colEntries.length > 0) {
      let minBal = colEntries[0].balance;
      let maxBal = colEntries[0].balance;
      for (let j = 1; j < colEntries.length; j++) {
        if (colEntries[j].balance < minBal) minBal = colEntries[j].balance;
        if (colEntries[j].balance > maxBal) maxBal = colEntries[j].balance;
      }
      columns.push({
        entries: colEntries,
        minBalance: minBal,
        maxBalance: maxBal
      });
    }
  }

  const columnElements = [];
  for (let i = 0; i < columns.length; i++) {
    columnElements.push(
      <LedgerColumn 
        key={i}
        column={columns[i]} 
        colIndex={i} 
        entriesPerColumn={entriesPerColumn}
        horizonDays={horizonDays}
        onEditTransaction={onEditTransaction}
        transactions={transactions || []}
      />
    );
  }

  return (
    <div className="ledger-table-container" data-testid="ledger-container">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {columnElements}
      </div>
    </div>
  );
}
